// caster-delta.ts — surgical caster_update_delta handler.
//
// The companion to handleCasterUpdate (the full-push path). Instead of replacing the
// whole casterCache entry with a freshly-recompiled ~MB blob, the server diffs the
// pre-action blob against the recompiled one at SECTION boundaries (computed.vitality,
// computed.creature.<cluster>, inventory, statuses, encounter_creatures, …) and ships
// only the changed section subtrees as { path, value } | { path, removed } changes.
// We patch them into the cached blob IN PLACE (preserving object identity) and run the
// IDENTICAL downstream side effects as the full path. [[project_surgical_ws_delta]]
//
// The recompile still happens server-side (it is the source of truth for unpredictable
// Janus fan-out); only the PUSH shrinks. Per-creature `version` is the gap detector: an
// out-of-order / missed delta forces a full resync so deltas can stay small without drift.

import type { WSMessage } from './types';
import { createStateRecovery } from './state-recovery';
import { encounterCreatureField, type CasterArr } from '../shared/array-types';
import { applyCasterSideEffects } from './caster-update';
import { fetchAndCacheCaster } from './caster-cache';

type WSCasterUpdateDeltaMessage = Extract<WSMessage, { type: 'caster_update_delta' }>;
export type MutableDeltaRecord = Record<string, unknown>;

export function isMutableDeltaRecord(value: unknown): value is MutableDeltaRecord {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Set (or delete) a value at a dot-path inside a nested object, creating intermediate
 * objects as needed. Paths address SECTIONS, not scalars (e.g. `computed.creature.attacks`),
 * so a changed section's whole subtree is assigned at once — no per-leaf or array-element
 * diffing. Mutates `root` in place.
 */
function ftApplyPath(root: MutableDeltaRecord, path: string, value: unknown, remove: boolean): void {
    if (!path) return;
    const segs = path.split('.');
    let node: MutableDeltaRecord = root;
    for (let i = 0; i < segs.length - 1; i++) {
        const k = segs[i]!;
        const child = node[k];
        if (isMutableDeltaRecord(child)) {
            node = child;
        } else {
            const created: MutableDeltaRecord = {};
            node[k] = created;
            node = created;
        }
    }
    const leaf = segs[segs.length - 1]!;
    if (remove) delete node[leaf];
    else node[leaf] = value;
}

/**
 * Fall back to a full refetch when we cannot safely patch (no cached baseline, or a
 * version gap). Re-runs the shared side effects from the freshly-cached full blob so the
 * screen ends up in the correct state regardless of the missed delta(s).
 */
const resyncCaster = createStateRecovery('caster', async (casterId, signal) => {
    const before = window.FateTide?.casterCache?.get(casterId);
    const previousEncounterId = before ? String(encounterCreatureField(before.encounter_creatures, 'encounter_id') ?? '') : undefined;
    // Recovery must bypass the encounter seed and must not report an old cached row as success.
    await fetchAndCacheCaster(casterId, { skipEncounterSeed: true, throwOnError: true });
    signal.throwIfAborted();
    const fresh = window.FateTide?.casterCache?.get(casterId) as CasterArr | undefined;
    if (!fresh) throw new Error('Missing caster recovery payload');
    applyCasterSideEffects(fresh, casterId, undefined, previousEncounterId);
    return typeof fresh._ws_version === 'number' ? fresh._ws_version : undefined;
});

function handleCasterUpdateDelta(parsedData: WSCasterUpdateDeltaMessage): void {
    const casterId = parsedData.caster_id;
    if (!casterId || !checkIfValidUUID(casterId)) return;

    const cache = window.FateTide?.casterCache;
    const cached = cache?.get(casterId) as CasterArr | undefined;

    // No baseline to patch onto → pull a fresh full blob (then it's versioned for next time).
    if (!cached) {
        void resyncCaster(casterId, parsedData.version);
        return;
    }

    // Version gap detection. Deltas must apply in strict succession (baseline + 1). A
    // numeric gap (missed / reordered / reconnect) forces a full resync. A missing
    // baseline (un-versioned seed / legacy full push) adopts this delta optimistically —
    // the seed was current, so applying the first delta onto it is safe.
    const incoming = typeof parsedData.version === 'number' ? parsedData.version : undefined;
    const baseline =
        typeof (cached as Record<string, unknown>)._ws_version === 'number'
            ? ((cached as Record<string, unknown>)._ws_version as number)
            : undefined;
    if (incoming !== undefined && baseline !== undefined && incoming <= baseline) return;
    if (incoming !== undefined && baseline !== undefined && incoming !== baseline + 1) {
        ftDebugLog('caster', { deltaGap: casterId, baseline, incoming });
        void resyncCaster(casterId, parsedData.version);
        return;
    }

    const previousEncounterId = String(encounterCreatureField(cached.encounter_creatures, 'encounter_id') ?? '');
    const changes = Array.isArray(parsedData.changes) ? parsedData.changes : [];
    for (let i = 0; i < changes.length; i++) {
        const ch = changes[i];
        if (!ch || typeof ch.path !== 'string') continue;
        ftApplyPath(cached as unknown as MutableDeltaRecord, ch.path, ch.value, ch.removed === true);
    }

    if (incoming !== undefined) (cached as Record<string, unknown>)._ws_version = incoming;

    // Same object reference, re-set for any Map subscribers / consistency.
    cache?.set(casterId, cached);
    ftDebugLog('caster', { deltaApplied: casterId, count: changes.length, version: incoming });

    // Nothing actually changed → skip the repaint work.
    if (changes.length > 0) {
        void window.ftAdoptInventoryBase?.(cached);
        applyCasterSideEffects(cached, casterId, changes.map(change => change.path), previousEncounterId);
    }

    // Subscribe glue: re-render the open side-info drawer card in place (cache-only) if this delta
    // touched it. Only fold in the caster's inventory ids when an inventory/statuses section actually
    // changed — so a pure HP/vitality delta during combat does NOT churn an unrelated open item card.
    // [[open-side-info subscribe]]
    try {
        const w = window as unknown as {
            ftRerenderOpenSideInfo?: (affected?: Iterable<string>) => void;
            ftCollectInventoryIds?: (blob: unknown) => string[];
            isSideInfoPanelOpen?: () => boolean;
        };
        if (
            changes.length > 0
            && typeof w.ftRerenderOpenSideInfo === 'function'
        ) {
            const touchedInv = changes.some(
                (ch) => ch && typeof ch.path === 'string' && /^(inventory|statuses)\b/.test(ch.path),
            );
            const invIds = touchedInv && typeof w.ftCollectInventoryIds === 'function'
                ? w.ftCollectInventoryIds(cached)
                : [];
            w.ftRerenderOpenSideInfo([casterId, ...invIds]);
        }
    } catch (e) { /* non-fatal */ }

    // Live-stock glue for an open trade/trainer panel — see caster-update.ts. Gated on the delta
    // having actually changed something, matching the repaint gate above, and on that change
    // being one a trade panel could care about: inventory (stock + coin stacks) or the
    // encounter_creatures bucket (merchant_favor, position/reach). A pure HP/status tick during
    // combat must not trigger a full PHP fragment recompile.
    try {
        if (changes.length > 0) {
            const relevant = changes.some(
                (ch) => ch && typeof ch.path === 'string' && /^(inventory|encounter_creatures)\b/.test(ch.path),
            );
            (window as unknown as { ftExchangeOnParticipantUpdate?: (id: string, relevant?: boolean) => void })
                .ftExchangeOnParticipantUpdate?.(casterId, relevant);
        }
    } catch (e) { /* non-fatal */ }
}

window.handleCasterUpdateDelta = handleCasterUpdateDelta;

export { handleCasterUpdateDelta, ftApplyPath, resyncCaster };
